import {Injectable, Optional} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {ApiConfig} from '../../../../../api-endpoint/src/lib/api.config';
import {OrderHistory, OrderHistorySearch} from "@kion/data-models";

type IResponseItem = any;

export interface OrderHistoryPagination {
    page: number;
    perPage: number;
    totalPages: number;
    count: number;
    totalCount: number;
}

interface SearchResponse {
    results: IResponseItem[],
    pagination: OrderHistoryPagination,
}

@Injectable({
    providedIn: 'root'
})
export class OrderHistoryConnectors {

    private readonly _rootUrl: string = '';
    private readonly _baseSite: string = '';

    private historyRoute = '/access/orderhistory';

    constructor(
        @Optional() apiConfig: ApiConfig,
        private http: HttpClient,
    ) {
        this._rootUrl = apiConfig.rootUrl;
        this._baseSite = apiConfig.baseSite;
    }

    private static transformFromApi(response: IResponseItem): OrderHistory {
        return {
            id: response.code,
            ...response
        };
    }

    loadById(orderNumber: string): Observable<OrderHistory> {
        return this.http.get<IResponseItem>(this.getOrderHistoryEntryUrl(orderNumber)).pipe(
            map(
                (response: IResponseItem) => OrderHistoryConnectors.transformFromApi(response)
            ),
            catchError(error => throwError(error))
        )
    }

    search(
        query: string,
        page: number,
        pageSize: number,
        myOrders: boolean,
        truckId: string,
        sort: string,
    ): Observable<OrderHistorySearch> {
        let searchOptions = 'currentPage=' + (page - 1) + '&pageSize=' + pageSize;
        if (query) {
            searchOptions = searchOptions + '&query=' + query;
        }
        if (myOrders) {
            searchOptions = searchOptions + '&myOrders=' + myOrders;
        }
        if (truckId) {
            searchOptions = searchOptions + '&truckId=' + truckId;
        }
        if (sort) {
            searchOptions = searchOptions + '&sort=' + sort;
        }
        return this.http.get<SearchResponse>(this.getOrderHistoryUrl() + '?' + searchOptions).pipe(
            map(
                (response: SearchResponse) => {
                    return {
                        pagination: response.pagination,
                        orders: response.results.map((item: IResponseItem) => OrderHistoryConnectors.transformFromApi(item)),
                    }
                }
            ),
            catchError(
                error => throwError(error)
            )
        )
    }

    update(
        orderId: string,
        note: string,
        poNumber: string
    ): Observable<OrderHistory> {
        const request = {
            userComment: {
                text: note
            },
            purchaseOrderNumber: poNumber
        };
        return this.http.post<void>(this.getUpdateUrl(orderId), request).pipe(
            map(
                (response: IResponseItem) => OrderHistoryConnectors.transformFromApi(response)
            ),
            catchError(error => throwError(error))
        )
    }

    protected getOrderHistoryUrl() {
        return this._rootUrl + this._baseSite + this.historyRoute;
    }

    protected getOrderHistoryEntryUrl(id: string) {
        return this.getOrderHistoryUrl() + "/" + id;
    }

    protected getUpdateUrl(id: string): string {
        return this.getOrderHistoryUrl() + "/" + id;
    }
}
